import { CheckCircle2, GitMerge } from "lucide-react";
import type { MergePolicy, Team } from "@/lib/api/types";
import { useSetMergePolicy, useTeamMembers } from "@/hooks/queries/use-teams";
import { useOnboardingState } from "@/hooks/queries/use-onboarding";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

/** The three merge policies, each with one line of plain-language help (ENG-343). */
export const MERGE_POLICY_OPTIONS: { value: MergePolicy; label: string; help: string }[] = [
  { value: "never", label: "Never", help: "Agents open pull requests; a person merges." },
  { value: "ask", label: "Ask me", help: "The build asks you to approve each merge." },
  { value: "auto", label: "Automatic", help: "Merge when the build and wrap-up checks pass." },
];

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}

/**
 * Project setting: who merges the agents' pull requests (ENG-343).
 * Project admins, organization admins and platform admins can change it;
 * everyone else sees the current policy read-only.
 */
export function MergePolicyCard({ team }: { team: Team }) {
  const me = useAuthStore((s) => s.user);
  const { data: onboarding } = useOnboardingState();
  const { data: members } = useTeamMembers(team.id);
  const setPolicy = useSetMergePolicy(team.id);

  const current: MergePolicy = setPolicy.data?.data.mergePolicy ?? team.mergePolicy ?? "never";
  const pending = setPolicy.isPending ? setPolicy.variables : undefined;
  const selected = pending ?? current;
  const myRole = members?.find((m) => m.user.id === me?.id)?.role;
  const canEdit =
    !!me?.isGlobalAdmin ||
    (onboarding?.step === "ready" && onboarding.organizationRole === "admin") ||
    myRole === "admin";

  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-medium">
          <GitMerge className="h-4 w-4" />
          Merging
        </h3>
        <p className="text-sm text-muted-foreground">
          Who merges the pull requests the agents open for this project.
        </p>
      </div>

      <div role="radiogroup" aria-label="Merge policy" className="space-y-2">
        {MERGE_POLICY_OPTIONS.map((option) => {
          const isSelected = option.value === selected;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={!canEdit || setPolicy.isPending}
              onClick={() => {
                if (option.value !== current) setPolicy.mutate(option.value);
              }}
              className={cn(
                "flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                isSelected ? "border-primary/60 bg-primary/10" : "border-white/[0.08]",
                canEdit ? "hover:border-primary/40" : "cursor-default",
                !canEdit && !isSelected && "opacity-60"
              )}
            >
              <span className="mt-0.5 h-4 w-4 shrink-0">
                {isSelected ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
              </span>
              <span>
                <span className="block text-sm font-medium">{option.label}</span>
                <span className="block text-xs text-muted-foreground">{option.help}</span>
              </span>
            </button>
          );
        })}
      </div>

      {!canEdit ? (
        <p className="text-xs text-muted-foreground">Only a project admin can change this.</p>
      ) : null}
      {errorText(setPolicy.error) ? (
        <p className="text-xs text-destructive">{errorText(setPolicy.error)}</p>
      ) : null}
    </div>
  );
}
